import * as React from 'react';
import { useMemo } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import './app.css';

// mui
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { pages } from './pages';
import { InopackDrawer } from './app/inopack-drawer/inopack-drawer';
import { NotFoundPage } from './pages/not-found/not-found-page';
import { DAppContent } from './dum';
import { useCurrentUserQuery } from '../services';
import { routeAuthFilter } from '../types';
import { InopackAppBar } from './app/inopack-app-bar/inopack-app-bar';

// Sends the user to the first route their roles can actually see. Domain roles
// can't see the dashboard, so a hardcoded redirect there would dead-end them on
// the Not Found page.
function LandingRedirect() {
    const { data, loading } = useCurrentUserQuery();
    const currentUser = data?.currentUser;
    if (loading || !currentUser) return null;
    const firstAccessible = pages.find(routeAuthFilter(currentUser));
    return (
        <Navigate
            to={firstAccessible ? firstAccessible.path : '/dashboard'}
            replace
        />
    );
}

export default function App() {
    const theme = useTheme();
    const mdAndUp = useMediaQuery(theme.breakpoints.up('lg'));

    const [open, setOpen] = React.useState(false);

    const { data } = useCurrentUserQuery();

    const currentUser = data?.currentUser;

    const toggleDrawer = () => {
        setOpen(!open);
    };

    const routeFilter = useMemo(() => {
        return currentUser ? routeAuthFilter(currentUser) : () => {};
    }, [currentUser]);

    // Browser-tab title follows the active route. Match the current pathname to
    // the deepest page whose path is a prefix of it (so nested routes like
    // /orderRequests/123 still resolve to "Pedidos"), then show
    // "Inopack - <Page>". Falls back to plain "Inopack" off any known page.
    const location = useLocation();
    React.useEffect(() => {
        const { pathname } = location;
        const match = pages
            .filter(
                (page) =>
                    !!page.path &&
                    (pathname === page.path ||
                        pathname.startsWith(`${page.path}/`)),
            )
            .sort((a, b) => b.path.length - a.path.length)[0];

        document.title = match?.title ? `Inopack - ${match.title}` : 'Inopack';
    }, [location]);

    return (
        <Box sx={{ display: 'flex' }}>
            {!mdAndUp ? <InopackAppBar toggleDrawer={toggleDrawer} /> : null}

            <InopackDrawer
                open={open}
                setOpen={setOpen}
                toggleDrawer={toggleDrawer}
                isDesktop={mdAndUp}
            />
            <DAppContent isDesktop={mdAndUp}>
                <Routes>
                    {pages
                        .filter(routeFilter)
                        .map(({ name, path, isNested, component: Elem }) => {
                            return (
                                <Route
                                    element={Elem}
                                    key={name}
                                    path={`${path}${isNested ? '/*' : ''}`}
                                />
                            );
                        })}
                    <Route path="/" element={<LandingRedirect />} />
                    <Route path={'*'} element={<NotFoundPage />} />
                </Routes>
            </DAppContent>
        </Box>
    );
}
